import { Check, Layers } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { BRAND_DOMAIN_READY } from "@/config/dimensions";

interface GroupByFilterProps {
  /** Grouping dimensions, in order. One = normal; two/three = multi-group. */
  value: string[];
  onChange: (value: string[]) => void;
}

type Option = { value: string; label: string };

const CORE: Option[] = [
  { value: "campaign", label: "Campaign" },
  ...(BRAND_DOMAIN_READY
    ? [
        { value: "brand", label: "Brand" },
        { value: "domain", label: "Domain" },
        { value: "url", label: "URL" },
      ]
    : []),
  { value: "offer", label: "Offer" },
  { value: "source", label: "Traffic Source" },
  { value: "affiliate_network", label: "Affiliate Network" },
  { value: "campaign_group", label: "Campaign Group" },
  { value: "stream", label: "Flow (Stream)" },
  { value: "landing_page", label: "Landing Page" },
];

const GEO: Option[] = [
  { value: "country", label: "Country (GEO)" },
  { value: "region", label: "Region" },
  { value: "city", label: "City" },
  { value: "language", label: "Language" },
];

const LABELS = new Map([...CORE, ...GEO].map((o) => [o.value, o.label]));

// More than three grouping columns is unreadable and explodes cardinality.
const MAX_DIMS = 3;

export function GroupByFilter({ value, onChange }: GroupByFilterProps) {
  const selected = value.length > 0 ? value : ["domain"];

  const toggle = (dim: string) => {
    if (selected.includes(dim)) {
      // Keep at least one grouping dimension.
      if (selected.length === 1) return;
      onChange(selected.filter((d) => d !== dim));
    } else {
      if (selected.length >= MAX_DIMS) return;
      onChange([...selected, dim]); // append — grouping order matters
    }
  };

  const summary = selected.map((d) => LABELS.get(d) ?? d).join(" ▸ ");

  const renderGroup = (title: string, options: Option[]) => (
    <div className="space-y-0.5">
      <div className="px-2 py-1 text-xs font-medium text-muted-foreground">
        {title}
      </div>
      {options.map((o) => {
        const on = selected.includes(o.value);
        const atCap = !on && selected.length >= MAX_DIMS;
        const order = selected.indexOf(o.value) + 1;
        return (
          <button
            key={o.value}
            type="button"
            disabled={atCap}
            onClick={() => toggle(o.value)}
            className={cn(
              "flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm transition-colors",
              on ? "bg-primary/10 text-primary" : "hover:bg-muted",
              atCap && "cursor-not-allowed opacity-40",
            )}
          >
            <span className="flex size-4 shrink-0 items-center justify-center">
              {on ? <Check className="size-3.5" /> : null}
            </span>
            <span className="flex-1 text-left">{o.label}</span>
            {on && selected.length > 1 ? (
              <span className="text-[10px] font-semibold text-primary">
                {order}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-medium text-muted-foreground">Group By:</span>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex h-9 min-w-[180px] max-w-[360px] cursor-pointer items-center gap-2 rounded-md border border-border bg-card px-3 text-sm"
          >
            <Layers className="size-4 shrink-0 text-muted-foreground" />
            <span className="truncate">{summary}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-1">
          {renderGroup("Core", CORE)}
          <div className="my-1 h-px bg-border" />
          {renderGroup("GEO (click)", GEO)}
          <div className="border-t border-border px-2 py-1.5 text-[11px] text-muted-foreground">
            Pick up to {MAX_DIMS}. Order sets the column order.
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
